import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DailyUnscheduledSessions from '../components/planner/DailyUnscheduledSessions';
import DailyAvailabilityGrid from '../components/planner/DailyAvailabilityGrid';
import DailyScheduledSessions from '../components/planner/DailyScheduledSessions';
import MultiStepScheduleModal from '../components/planner/MultiStepScheduleModal';
import PlanningMobileFallback from '../components/planner/PlanningMobileFallback';
import PlanningDateNavigator from '../components/planner/PlanningDateNavigator';
import PlanningCrewFilter from '../components/planner/PlanningCrewFilter';
import { useNotifications } from '../components/notifications';
import { planningAPI, crewsAPI } from '../services/api';
import { loadBoatTypes } from '../utils/boatTypes';
import LoadingSpinner from '../components/LoadingSpinner';
import { XMarkIcon } from '@heroicons/react/24/outline';

const Planning = () => {
  const { showSuccess, showError, showInfo } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Initialize date from URL params or default to next Monday
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      return dateParam;
    }

    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysUntilNextMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek; // If Sunday, next Monday is 1 day away, otherwise 8 - dayOfWeek
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilNextMonday);
    return nextMonday.toISOString().split('T')[0];
  });

  // Data state
  const [planningData, setPlanningData] = useState(null);
  const [allCrews, setAllCrews] = useState([]);

  // Initialize selected crews from URL params
  const [selectedCrews, setSelectedCrews] = useState(() => {
    const crewsParam = searchParams.get('crews');
    if (crewsParam) {
      return crewsParam.split(',').map((id) => parseInt(id, 10));
    }
    return [];
  });

  // Track selected unscheduled session for crew filtering
  const [selectedUnscheduledSession, setSelectedUnscheduledSession] = useState(null);

  // UI state
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, [currentDate]);

  // Load boat types on component mount
  useEffect(() => {
    loadBoatTypes();
  }, []);

  // Auto-filter crews based on unscheduled sessions
  useEffect(() => {
    if (
      planningData?.unscheduled_sessions &&
      planningData.unscheduled_sessions.length > 0 &&
      selectedCrews.length === 0
    ) {
      // Get unique crew IDs from all unscheduled sessions
      const sessionCrewIds = new Set();
      planningData.unscheduled_sessions.forEach((session) => {
        if (session.assigned_crews && session.assigned_crews.length > 0) {
          session.assigned_crews.forEach((assignment) => {
            if (assignment.id) {
              sessionCrewIds.add(parseInt(assignment.id));
            }
          });
        }
      });

      // Auto-select these crews if no crews are currently selected
      if (sessionCrewIds.size > 0) {
        const crewIds = Array.from(sessionCrewIds);
        setSelectedCrews(crewIds);
        showInfo(`Auto-selected ${crewIds.length} crews from unscheduled sessions`);
      }
    }
  }, [planningData?.unscheduled_sessions, selectedCrews.length, showInfo]);

  // Update URL when date or crews change
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('date', currentDate);
    if (selectedCrews.length > 0) {
      params.set('crews', selectedCrews.join(','));
    }
    setSearchParams(params, { replace: true });
  }, [currentDate, selectedCrews, setSearchParams]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load crews if not loaded
      if (allCrews.length === 0) {
        const crewsResponse = await crewsAPI.getCrews();
        setAllCrews(crewsResponse);
      }

      // Load planning data - always without crew filtering to show all unscheduled sessions
      // The availability grid will be filtered in the frontend based on selectedCrews
      const data = await planningAPI.getDailyPlanningData(currentDate, null);
      setPlanningData(data);
    } catch (err) {
      console.error('Error loading planning data:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to load planning data';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const navigateDay = (direction) => {
    const current = new Date(currentDate);
    const newDate = new Date(current.setDate(current.getDate() + direction));
    setCurrentDate(newDate.toISOString().split('T')[0]);
  };

  const handleDateSelect = (selectedDate) => {
    setCurrentDate(selectedDate);
  };

  const handleUnscheduledSessionSelect = useCallback(
    (session) => {
      // If the same session is clicked again, deselect it
      if (selectedUnscheduledSession?.id === session.id) {
        setSelectedUnscheduledSession(null);

        // Revert to showing all crews from all unscheduled sessions
        if (planningData?.unscheduled_sessions) {
          const allSessionCrewIds = new Set();
          planningData.unscheduled_sessions.forEach((s) => {
            if (s.assigned_crews && s.assigned_crews.length > 0) {
              s.assigned_crews.forEach((assignment) => {
                if (assignment.id) {
                  allSessionCrewIds.add(parseInt(assignment.id));
                }
              });
            }
          });

          if (allSessionCrewIds.size > 0) {
            const crewIds = Array.from(allSessionCrewIds);
            setSelectedCrews(crewIds);
            showInfo(
              `Session deselected - showing all ${crewIds.length} crews from unscheduled sessions`
            );
          } else {
            setSelectedCrews([]);
            showInfo('Session deselected - no crews to show');
          }
        } else {
          setSelectedCrews([]);
          showInfo('Session deselected - showing all crews');
        }
        return;
      }

      setSelectedUnscheduledSession(session);

      // Extract crew IDs from the selected session
      if (session.assigned_crews && session.assigned_crews.length > 0) {
        const sessionCrewIds = session.assigned_crews.map((assignment) => parseInt(assignment.id));
        setSelectedCrews(sessionCrewIds);
        showInfo(`Selected "${session.title}" - showing ${sessionCrewIds.length} assigned crews`);
      } else {
        // If session has no crew assignments, clear the crew selection but keep the session selected
        setSelectedCrews([]);
        showInfo(`Selected "${session.title}" - no specific crews assigned`);
      }
    },
    [selectedUnscheduledSession, setSelectedCrews, showInfo, planningData?.unscheduled_sessions]
  );

  const handleCrewFilterChange = (crewId) => {
    // Clear selected session when manually changing crew filter
    setSelectedUnscheduledSession(null);

    const id = Number(crewId);
    if (!Number.isFinite(id)) return;

    setSelectedCrews((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSessionDrop = useCallback(
    (item, date, time) => {
      const sessionId = item.id;
      const session = item.session;

      if (!session) {
        showError('Invalid session data');
        return;
      }

      console.log(
        `Dropped session ${session.title} (ID: ${sessionId}) onto timeslot ${date} at ${time}`
      );
      setSelectedSession(sessionId);
      setSelectedTimeSlot({ date, time });
      setShowScheduleModal(true);

      showInfo(`Opening scheduler for "${session.title}"`);
    },
    [setSelectedSession, setSelectedTimeSlot, setShowScheduleModal, showError, showInfo]
  );

  const handleScheduleSession = useCallback(
    async (result) => {
      try {
        setLoading(true);

        // The result comes from the new save session configuration API
        if (result.success) {
          setShowScheduleModal(false);
          setSelectedSession(null);
          setSelectedTimeSlot(null);
          showSuccess('Session scheduled successfully!');
          loadInitialData(); // Refresh data after scheduling
        } else {
          throw new Error('Failed to schedule session');
        }
      } catch (err) {
        console.error('Failed to schedule session:', err);
        const errorMessage =
          err.response?.data?.detail || err.message || 'Failed to schedule session.';
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [showSuccess, showError, setError, loadInitialData]
  );

  const handleCloseScheduleModal = useCallback(() => {
    setShowScheduleModal(false);
    setSelectedSession(null);
    setSelectedTimeSlot(null);
  }, [setShowScheduleModal, setSelectedSession, setSelectedTimeSlot]);

  if (loading && !planningData) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h3 className="mb-2 text-lg font-medium text-gray-900">Error Loading Planning Data</h3>
          <p className="mb-4 text-gray-600">{error}</p>
          <button
            onClick={loadInitialData}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full overflow-hidden bg-gray-50">
      {/* Mobile placeholder */}
      <PlanningMobileFallback />

      {/* Desktop Layout */}
      <div className="hidden h-full w-full max-w-full overflow-hidden lg:flex lg:flex-col">
        {/* Main Content - Full Height Layout */}
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Unscheduled Sessions with Overlays - Top Panel */}
          <div className="relative flex-shrink-0">
            {/* Day Navigation - Top Center Overlay */}
            <PlanningDateNavigator
              currentDate={currentDate}
              navigateDay={navigateDay}
              onDateSelect={handleDateSelect}
            />
            {/* Unscheduled Sessions with Crew Filter */}
            <DailyUnscheduledSessions
              sessions={planningData?.unscheduled_sessions}
              allCrews={allCrews}
              selectedCrews={selectedCrews}
              handleCrewFilterChange={handleCrewFilterChange}
              setSelectedCrews={setSelectedCrews}
              selectedUnscheduledSession={selectedUnscheduledSession}
              onSessionSelect={handleUnscheduledSessionSelect}
            />
          </div>

          {/* Availability Grids - Expandable Middle Panel */}
          <div className="min-h-0 flex-1">
            <DailyAvailabilityGrid
              planningData={planningData}
              selectedCrews={selectedCrews}
              onSessionDrop={handleSessionDrop}
            />
          </div>

          {/* Scheduled Sessions - Bottom Panel */}
          <div className="flex-shrink-0">
            <DailyScheduledSessions planningData={planningData} />
          </div>
        </div>

        {/* Schedule Session Modal */}
        {showScheduleModal && selectedSession && selectedTimeSlot && (
          <MultiStepScheduleModal
            session={planningData?.unscheduled_sessions?.find(
              (s) => s.id === (selectedSession.id || selectedSession)
            )}
            timeSlot={selectedTimeSlot}
            onSchedule={handleScheduleSession}
            onClose={handleCloseScheduleModal}
          />
        )}
      </div>
    </div>
  );
};

export default Planning;
